import { Skeleton } from '@/components/atoms';
import { InfoCircleIcon } from '@/components/atoms/icons';
import { Typography } from '@/components/atoms/Typography';
import { useTranslation, useUIColors } from '@/core/hooks';
import { isPhoneScreen } from '@/core/utils/responsive';
import { useI18nContext } from '@/core/providers/I18nProvider';
import {
  mapSalesforceReservationStatus,
} from '@/core/reservation';
import {
  ensureAdhaReservationStatusCopy,
  getValue,
  hasAdhaReservationStatusCopy,
} from '@/core/services/translationService';
import { getReservationVisibleCopy } from '@/modules/properties-web/constants/webReservationStatus';
import React, { useEffect, useState } from 'react';
import { Platform, StyleSheet, useWindowDimensions, View, type TextLayoutEvent, type TextStyle, type ViewStyle } from 'react-native';

const ICON_SIZE = 16;

export interface AdhaUnitStatusBadgeProps {
  /** Profile-details unit `status` (raw Salesforce value). */
  rawStatus?: string | null | undefined;
  /**
   * Web: listing uses Font/Size/XS Medium, details use Font/Size/Base SemiBold.
   * Mobile: both placements use Font/Size/3XS Regular.
   */
  placement?: 'listing' | 'detail';
}

function translateStatusKey(locale: 'en' | 'ar', key: string): string {
  const adhaKey = `adha.${key}`;
  const adhaValue = getValue(locale, adhaKey);
  if (adhaValue && adhaValue !== adhaKey) {
    return adhaValue;
  }
  return getValue(locale, key);
}

function resolveStatusMessage(locale: 'en' | 'ar', rawStatus: string): string | null {
  const mappedStatus = mapSalesforceReservationStatus(rawStatus);
  const copy = getReservationVisibleCopy(
    mappedStatus,
    (key) => translateStatusKey(locale, key),
    rawStatus,
  );
  const message = copy?.listingMessage?.trim() ?? '';
  if (!mappedStatus || !message) {
    return null;
  }
  return message;
}

function chipPlacementStyle(isListing: boolean, detailWrapped: boolean): ViewStyle {
  if (isListing) {
    return styles.listingChip;
  }
  if (detailWrapped) {
    return styles.detailChipWrapped;
  }
  return styles.detailChip;
}

function labelStyles(isListing: boolean, compact: boolean, detailWrapped: boolean): TextStyle[] {
  const variantStyle = isListing ? styles.listingLabel : styles.detailLabel;
  const stylesForLabel: TextStyle[] = [variantStyle];
  if (compact) {
    stylesForLabel.push(styles.compactLabel);
  }
  if (!detailWrapped) {
    return stylesForLabel;
  }
  stylesForLabel.push(styles.detailLabelWrapped);
  if (compact) {
    stylesForLabel.push(styles.detailLabelWrappedCompact);
  }
  return stylesForLabel;
}

function badgeViewModel(isListing: boolean, compact: boolean, detailWrapped: boolean, isRTL: boolean) {
  const iconStyle: ViewStyle[] = [styles.iconWrap];
  if (compact) {
    iconStyle.push(styles.iconWrapCompact);
  }
  if (detailWrapped) {
    iconStyle.push(styles.iconWrapWrapped);
  }

  return {
    chipStyle: chipPlacementStyle(isListing, detailWrapped),
    iconStyle,
    iconSize: compact ? 14 : ICON_SIZE,
    labelStyle: labelStyles(isListing, compact, detailWrapped),
    variant: isListing ? ('subhead' as const) : ('headline' as const),
    numberOfLines: isListing ? 1 : undefined,
    ellipsizeMode: isListing ? ('tail' as const) : undefined,
    trackWrapping: !isListing,
    flexDirection: isRTL ? ('row-reverse' as const) : ('row' as const),
  };
}

/**
 * Status chip for an owned unit that is still an ADHA unit (`isADHAUnit`).
 * Copy matches the ADHA reservation lookup: Contentful
 * `adha.reservationStatus.<lowercased status>`, otherwise the raw status.
 */
export function AdhaUnitStatusBadge({
  rawStatus,
  placement = 'detail',
}: Readonly<AdhaUnitStatusBadgeProps>) {
  const colors = useUIColors();
  const { width } = useWindowDimensions();
  const compact = isPhoneScreen(width);
  const { isRTL } = useI18nContext();
  const { currentLanguage } = useTranslation();
  const locale = currentLanguage === 'ar' ? 'ar' : 'en';
  const trimmedStatus = rawStatus?.trim() ?? '';
  const [readyLocale, setReadyLocale] = useState<'en' | 'ar' | null>(() =>
    hasAdhaReservationStatusCopy(locale) ? locale : null,
  );
  const [detailWrapped, setDetailWrapped] = useState(false);

  useEffect(() => {
    if (!trimmedStatus) {
      return;
    }
    let cancelled = false;
    void ensureAdhaReservationStatusCopy(locale).finally(() => {
      if (!cancelled) {
        setReadyLocale(locale);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [locale, trimmedStatus]);

  if (!trimmedStatus) {
    return null;
  }

  const isReady =
    readyLocale === locale || hasAdhaReservationStatusCopy(locale);
  if (!isReady) {
    return (
      <Skeleton width={180} height={28} borderRadius={8} />
    );
  }

  const message = resolveStatusMessage(locale, trimmedStatus);
  if (!message) {
    return null;
  }

  const isListing = placement === 'listing';
  const textColor = colors.adhaUnitStatusText;
  const backgroundColor = colors.adhaUnitStatusBg;
  const view = badgeViewModel(isListing, compact, detailWrapped, isRTL);

  const handleDetailTextLayout = (event: TextLayoutEvent) => {
    const wrapped = event.nativeEvent.lines.length > 1;
    setDetailWrapped((current) => (current === wrapped ? current : wrapped));
  };

  return (
    <View
      style={[
        styles.chip,
        view.chipStyle,
        {
          backgroundColor,
          flexDirection: view.flexDirection,
        },
      ]}
    >
      <View style={view.iconStyle}>
        <InfoCircleIcon size={view.iconSize} color={textColor} />
      </View>
      <Typography
        variant={view.variant}
        color={textColor}
        style={view.labelStyle}
        numberOfLines={view.numberOfLines}
        ellipsizeMode={view.ellipsizeMode}
        onTextLayout={view.trackWrapping ? handleDetailTextLayout : undefined}
      >
        {message}
      </Typography>
    </View>
  );
}

/** Web line boxes sit high of the icon. Same nudge on listing and details. */
const webLabelAlign =
  Platform.OS === 'web'
    ? ({
        marginTop: 2,
        WebkitFontSmoothing: 'antialiased',
        MozOsxFontSmoothing: 'grayscale',
      } as object)
    : null;

const styles = StyleSheet.create({
  chip: {
    alignSelf: 'flex-start',
    alignItems: 'center',
    gap: 6,
    maxWidth: '100%',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 8,
  },
  listingChip: {
    height: 24,
    justifyContent: 'center',
    borderRadius: 8,
    maxWidth: '100%',
    minWidth: 0,
    flexShrink: 1,
  },
  /** One line stays 32px. Two or three lines grow, with 4px above and below. */
  detailChip: {
    minHeight: 32,
    justifyContent: 'center',
    borderRadius: 8,
    paddingTop: 4,
    paddingBottom: 4,
  },
  detailChipWrapped: {
    minHeight: 32,
    alignItems: 'flex-start',
    borderRadius: 8,
    paddingTop: 4,
    paddingBottom: 4,
  },
  iconWrap: {
    width: ICON_SIZE,
    height: ICON_SIZE,
    flexShrink: 0,
    alignItems: 'center',
    justifyContent: 'center',
  },
  /** Sit the icon on the first line once the label wraps. */
  iconWrapCompact: {
    width: 14,
    height: 14,
  },
  iconWrapWrapped: {
    marginTop: 4,
  },
  /**
   * Detail: web keeps Font/Size/Base SemiBold 16.
   * Mobile: Font/Size/3XS Regular 12, LH 100%, letter-spacing 0.
   */
  detailLabel: {
    flexShrink: 1,
    fontFamily: Platform.OS === 'web' ? 'Matter-SemiBold' : 'Matter-Regular',
    fontWeight: '400',
    fontSize: Platform.OS === 'web' ? 16 : 12,
    lineHeight: Platform.OS === 'web' ? 16 : 12,
    letterSpacing: 0,
    includeFontPadding: false,
    textAlignVertical: 'center',
    ...webLabelAlign,
  },
  detailLabelWrapped: {
    lineHeight: 24,
    marginTop: 0,
    ...(Platform.OS === 'web' ? ({ display: 'block' } as object) : null),
  },
  detailLabelWrappedCompact: {
    lineHeight: 18,
  },
  compactLabel: {
    fontSize: 12,
  },
  /**
   * Listing: web keeps Font/Size/XS Medium 14.
   * Mobile: Font/Size/3XS Regular 12, LH 100%, letter-spacing 0.
   */
  listingLabel: {
    flexShrink: 1,
    minWidth: 0,
    ...(Platform.OS === 'web'
      ? ({
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        } as object)
      : null),
    fontFamily: Platform.OS === 'web' ? 'Matter-Medium' : 'Matter-Regular',
    fontWeight: '400',
    fontSize: Platform.OS === 'web' ? 14 : 12,
    lineHeight: Platform.OS === 'web' ? 16 : 12,
    letterSpacing: 0,
    includeFontPadding: false,
    textAlignVertical: 'center',
    ...webLabelAlign,
  },
});
